import React from "react";
import type { ApolloClient } from "@apollo/client";
import moment from "moment";
import { compact, find, get, includes, isEmpty, reject } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Form, OverlayTrigger, Popover } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { emailValidator } from "utils/fieldValidators";
import type { RootState } from "types/state";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import {
  getUser,
  lockTargetUser,
  requirePortfolioUpdate,
  resetUserOnboardingState,
  unlockTargetUser,
  updateUserEmail,
} from "actions/adminActions";
import {
  userIs401kRollInPaychex,
  userIsClosingAccountSelector,
  userIsLockedSelector,
} from "store/selectors/user";
import {
  IndividualPortfolioUpdate,
  IndividualRequiredUpdatesPending,
  individualPreKycSubmissionStates,
  lifecycleStates,
} from "statics/states";
import { identityProviderLabel } from "statics/identityProviders";

import InfoTable from "components/InfoTable";
import Button from "components/Button";
import SimpleModal from "components/SimpleModal";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import { toast } from "react-toastify";
import { push } from "store/actions/router";
import { ROTH_IRA, TRADITIONAL_IRA } from "statics/accountTypes";
import { Link } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import { isSuperIconAdmin } from "store/selectors/admin";
import AdvanceAlloyConfirmationModal from "./AdvanceAlloyConfirmationModal";
import AdminUserProfileWorkflow from "./AdminUserProfileWorkflow";

// Actions render inline as buttons up to this count; beyond it they collapse
// into a single "Actions" dropdown to keep the row from getting crowded.
const MAX_INLINE_ACTIONS = 3;

const errorSelector = createErrorSelector([
  adminConstants.LOCK_USER,
  adminConstants.UNLOCK_USER,
  adminConstants.GET_USER,
  adminConstants.REQUIRE_PORTFOLIO_UPDATE,
  adminConstants.RESET_USER_STATE,
  adminConstants.UPDATE_USER_EMAIL,
]);

const lockingSelector = createLoadingSelector([
  adminConstants.LOCK_USER,
  adminConstants.UNLOCK_USER,
]);

const requiringPortfolioSelector = createLoadingSelector(
  adminConstants.REQUIRE_PORTFOLIO_UPDATE
);

const resetingSelector = createLoadingSelector([
  adminConstants.RESET_USER_STATE,
  adminConstants.GET_USER,
]);

const updatingEmailSelector = createLoadingSelector(
  adminConstants.UPDATE_USER_EMAIL
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isTogglingLockStatus: lockingSelector(state),
    isRequiringPortfolio: requiringPortfolioSelector(state),
    isResetingUser: resetingSelector(state),
    isSuperAdmin: isSuperIconAdmin(state),
    isUpdatingEmail: updatingEmailSelector(state),
  };
};

const mapDispatchToProps = {
  getUser,
  lockTargetUser,
  unlockTargetUser,
  requirePortfolioUpdate,
  resetUserOnboardingState,
  updateUserEmail,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // Supplied by the parent User page; the only prop connect/withApollo do not
  // inject. Optional to match the original PropTypes (PropTypes.string).
  userId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

/** The getUserDetails payload the page stores wholesale in state.user. */
interface UserDetails {
  user?: { state?: string } & Record<string, unknown>;
  userProfile?: Record<string, unknown>;
  accounts?: Array<{ accountType?: string; accountNumber?: string }>;
  fullSsn?: string;
  [key: string]: unknown;
}

interface State {
  user: UserDetails;
  isFetching: boolean;
  showFullSsn: boolean;
  showConfirmAdvanceAlloyModal: boolean;
  showEmailEditModal: boolean;
  showEditProfileModal: boolean;
  showResetConfirmModal: boolean;
}

/** Initial values of the "Update User Email" Formik modal. */
interface EmailFormValues {
  newEmail: string;
  confirmEmail: string;
}

/** The userAuthorizations selection on the getUserDetails payload. */
interface UserAuthorizationSummary {
  id: string;
  identityProvider: string;
  identityProviderId: string;
  deleted: boolean;
}

/** A row in the actions menu/inline-button list built by _getActions. */
interface UserAction {
  key: string;
  label: string;
  onClick?: () => void;
  color?: "red";
  disabled?: boolean;
}

class UserInfo extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      user: {},
      isFetching: true,
      showFullSsn: false,
      showConfirmAdvanceAlloyModal: false,
      showEmailEditModal: false,
      showEditProfileModal: false,
      showResetConfirmModal: false,
    };
  }

  async componentDidMount() {
    await this._getAndSetUser();
    this.setState({
      isFetching: false,
    });
  }

  _getAndSetUser = async () => {
    // getUser resolves to the dispatched action; the SUCCESS branch carries
    // the getUserDetails payload under `data`. userId is cast to match
    // getUser's param without altering the runtime value (mirrors User.tsx).
    const { data: user } = (await this.props.getUser(
      this.props.client as ApolloClient<object>,
      this.props.userId as string
    )) as { data: UserDetails };
    this.setState({ user });
  };

  _resetUserState = async () => {
    const result = await this.props.resetUserOnboardingState(
      this.props.client as ApolloClient<object>,
      this.props.userId as string
    );
    this.setState({ showResetConfirmModal: false });
    if (result.success) {
      toast.success("Successfully reset user state.");
      this.props.push("/users");
    }
  };

  _unlockUser = async () => {
    const result = (await this.props.unlockTargetUser(
      this.props.client as ApolloClient<object>,
      this.props.userId as string
    )) as {
      type: string;
      success: boolean;
      data?: { state?: string };
    };
    if (result.success) {
      this._setUserStateFromAction(result.data?.state);
    }
  };

  _lockUser = async () => {
    const result = (await this.props.lockTargetUser(
      this.props.client as ApolloClient<object>,
      this.props.userId as string
    )) as {
      type: string;
      success: boolean;
      data?: { state?: string };
    };
    if (result.success) {
      this._setUserStateFromAction(result.data?.state);
    }
  };

  _setUserStateFromAction = (state?: string) => {
    this.setState({
      user: {
        ...this.state.user,
        user: {
          ...this.state.user.user,
          state,
        },
      },
    });
  };

  _advanceAlloyJourney = async () => {
    this.setState({ showConfirmAdvanceAlloyModal: true });
  };

  _canEditProfile = () => {
    if (!this.props.isSuperAdmin) {
      return false;
    }
    // Only allow editing once the user has finished onboarding/KYC and is in a
    // lifecycle state. lifecycleStates is disjoint from the onboarding and
    // pre-KYC submission state sets, so membership here is sufficient.
    const state = get(this.state.user, "user.state");
    return includes(lifecycleStates, state);
  };

  _getActions = (): UserAction[] => {
    const state = get(this.state.user, "user.state") as string | undefined;
    // The user selectors read nested paths off RootState; the page hands them
    // a synthetic slice shaped only with what they read. Cast at the boundary.
    const wrappedState = {
      user: { userState: { state } },
    } as unknown as RootState;
    const userIsLocked = userIsLockedSelector(wrappedState);
    const userIsClosingAccount = userIsClosingAccountSelector(wrappedState);
    const userIsPreKycSubmission = includes(
      individualPreKycSubmissionStates,
      state
    );
    const portfolioUpdateDisabled = [
      IndividualRequiredUpdatesPending,
      IndividualPortfolioUpdate,
    ].includes(state as string);

    const actions: UserAction[] = [
      {
        key: "requirePortfolio",
        label: "Require Portfolio Update",
        onClick: this._requirePortfolioUpdate,
        disabled: portfolioUpdateDisabled,
      },
    ];

    if (!userIsClosingAccount) {
      actions.push({
        key: "lock",
        label: userIsLocked ? "Unlock User" : "Lock User",
        color: userIsLocked ? undefined : "red",
        onClick: userIsLocked ? this._unlockUser : this._lockUser,
      });
    }

    if (this.props.isSuperAdmin) {
      actions.push({
        key: "advanceAlloy",
        label: "Advance Alloy Journey",
        color: "red",
        onClick: this._advanceAlloyJourney,
      });
    }

    if (userIsPreKycSubmission) {
      actions.push({
        key: "reset",
        label: "Reset User",
        color: "red",
        onClick: () => this.setState({ showResetConfirmModal: true }),
      });
    }

    return actions;
  };

  _renderInlineAction = ({
    key,
    label,
    onClick,
    color,
    disabled,
  }: UserAction) => (
    <Button
      key={key}
      type="button"
      name="action"
      btnLabel={label}
      color={color}
      disabled={disabled}
      onClick={onClick}
      size="sm"
      withArrow
    />
  );

  _renderMenuItem = ({ key, label, onClick, color, disabled }: UserAction) => (
    <Button
      key={key}
      type="button"
      name="action"
      btnLabel={label}
      color={color}
      disabled={disabled}
      size="sm"
      style={{ width: "100%" }}
      onClick={() => {
        // Dismiss the actions popover, then run the action.
        document.body.click();
        if (onClick) {
          onClick();
        }
      }}
    />
  );

  _getActionsContent = () => {
    const actions = this._getActions();

    // Show actions inline until there are enough to crowd the row, then
    // collapse them into a single "Actions" dropdown.
    if (actions.length <= MAX_INLINE_ACTIONS) {
      return actions.map(this._renderInlineAction);
    }

    return (
      <OverlayTrigger
        rootClose
        trigger="click"
        placement="bottom-start"
        overlay={
          <Popover className="action-popover">
            <Popover.Body>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  minWidth: 190,
                }}
              >
                {actions.map(this._renderMenuItem)}
              </div>
            </Popover.Body>
          </Popover>
        }
      >
        <Button type="button" name="action" size="sm">
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            Actions
            <FiChevronDown />
          </span>
        </Button>
      </OverlayTrigger>
    );
  };

  _requirePortfolioUpdate = async () => {
    const result = (await this.props.requirePortfolioUpdate(
      this.props.client as ApolloClient<object>,
      this.props.userId as string
    )) as {
      type: string;
      success: boolean;
      data?: { state?: string };
    };

    if (result.success) {
      this._setUserStateFromAction(result.data?.state);
    }
  };

  _submitEmailUpdate = async ({ newEmail }: EmailFormValues) => {
    const result = await this.props.updateUserEmail(
      this.props.client as ApolloClient<object>,
      {
        // userId is optional on OwnProps; UpdateUserEmailInput requires
        // string | number. Cast at the boundary without changing the value.
        userId: this.props.userId as string,
        newEmail,
      }
    );
    if (result.success) {
      toast.success("Email update submitted for approval.");
      this.setState({ showEmailEditModal: false });
    }
  };

  _getTableMetadata = () => {
    return [
      {
        key: "user.firstName",
        label: "First Name",
      },
      {
        key: "user.lastName",
        label: "Last Name",
      },
      {
        key: this.state.showFullSsn ? "fullSsn" : "userProfile.ssn",
        label: "SSN",
        format: (data: unknown) => {
          const ssn = data as string;
          const actionText = this.state.showFullSsn ? "Hide" : "Show";
          return (
            <p style={{ marginBottom: 0 }}>
              <span
                style={{
                  fontSize: 12,
                  color: "#0cb7c4",
                }}
                onClick={() =>
                  this.setState({ showFullSsn: !this.state.showFullSsn })
                }
              >
                ({actionText} Full SSN)
              </span>{" "}
              {ssn}
            </p>
          );
        },
      },
      {
        key: "userProfile.dob",
        label: "DOB",
      },
      {
        key: "userProfile",
        label: "Address",
        format(data: unknown = {}) {
          const userProfile = data as {
            address1?: string;
            address2?: string;
            city?: string;
            state?: string;
            postalCode?: string;
          };
          if (isEmpty(userProfile)) {
            return "";
          }
          // `address2 && ...` yields a string or the falsy value verbatim — the
          // original interpolated it as-is; String() preserves that output
          // while satisfying restrict-template-expressions.
          return `${String(userProfile.address1)}${String(
            userProfile.address2 && ", " + userProfile.address2
          )}, ${String(userProfile.city)}, ${String(userProfile.state)} ${String(
            userProfile.postalCode
          )} `;
        },
      },
      {
        key: "userProfile.email",
        label: "Email",
        format: (data: unknown) => (
          <span>
            {data as string}{" "}
            <span
              style={{ fontSize: 12, color: "#0cb7c4", cursor: "pointer" }}
              onClick={() => this.setState({ showEmailEditModal: true })}
            >
              (Edit)
            </span>
          </span>
        ),
      },
      {
        key: "user.userAuthorizations",
        label: "Login Type",
        format(data: unknown) {
          const authorizations = reject(
            compact(data as UserAuthorizationSummary[] | null),
            "deleted"
          );
          if (isEmpty(authorizations)) {
            return "Unknown";
          }
          return authorizations.map((authorization) => (
            <div key={authorization.id}>
              {identityProviderLabel(authorization.identityProvider)}{" "}
              <span style={{ fontSize: 12, color: "#858c9c" }}>
                ({authorization.identityProviderId})
              </span>
            </div>
          ));
        },
      },
      {
        key: "userProfile.phoneNumber",
        label: "Phone #",
      },

      {
        key: "user.id",
        label: "ID",
      },
      {
        key: "accounts",
        label: "Apex ID",
        format(data: unknown) {
          const accounts = data as Array<{
            accountType?: string;
            accountNumber?: string;
          }>;
          const iraAccount = find(accounts, (account) => {
            return (
              account.accountType === TRADITIONAL_IRA ||
              account.accountType === ROTH_IRA
            );
          });
          if (!iraAccount) return "";
          return iraAccount.accountNumber;
        },
      },
      {
        key: "accounts",
        label: "Sila Wallet ID",
        format(data: unknown) {
          const accounts = data as Array<{
            accountType?: string;
            accountNumber?: string;
          }>;
          const walletAccount = find(accounts, { accountType: "WALLET" });
          if (!walletAccount) return "";
          return walletAccount.accountNumber;
        },
      },
      {
        key: "user.state",
        label: "State",
      },
      {
        key: "user.createdAt",
        label: "Created At",
        format(data: unknown) {
          const createdAt = data as string | undefined;
          if (!createdAt) return "";
          return moment(createdAt).format("MMM D, YYYY");
        },
      },
    ];
  };

  render() {
    if (this.state.isFetching) {
      return <IconSpinner centered />;
    }
    const user = get(this.state, "user.user", {});
    // userIs401kRollInPaychex reads user.userAuthorizations off RootState; the
    // page hands it a synthetic slice. Cast at the boundary (selector untouched).
    const isPaychex = userIs401kRollInPaychex({
      user,
    } as unknown as RootState);

    return (
      <>
        {this.state.showEmailEditModal && (
          // @ts-expect-error buttonText is required on SimpleModalProps but
          // omitted here — inert because hideActions suppresses the submit
          // button. Preserved verbatim from the original JS.
          <SimpleModal
            show={this.state.showEmailEditModal}
            title="Update User Email"
            subtext="This will initiate an approval workflow. The email address will not change until the request has been approved."
            onClose={() => this.setState({ showEmailEditModal: false })}
            showCloseButton
            hideActions
          >
            <Formik
              initialValues={{ newEmail: "", confirmEmail: "" }}
              validationSchema={Yup.object({
                newEmail: emailValidator("Must be a valid email address")
                  .required("Email is required")
                  .test(
                    "email-unchanged",
                    "New email must be different from current email",
                    (value) =>
                      value !== get(this.state.user, "userProfile.email")
                  ),
                confirmEmail: Yup.string()
                  .required("Please confirm the email address")
                  .oneOf([Yup.ref("newEmail")], "Email addresses must match"),
              })}
              onSubmit={this._submitEmailUpdate}
            >
              {({
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                errors,
                touched,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>New Email Address</Form.Label>
                    <Form.Control
                      name="newEmail"
                      type="email"
                      value={values.newEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.newEmail && !!errors.newEmail}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.newEmail}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Email Address</Form.Label>
                    <Form.Control
                      name="confirmEmail"
                      type="email"
                      value={values.confirmEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.confirmEmail && !!errors.confirmEmail}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.confirmEmail}
                    </Form.Control.Feedback>
                  </Form.Group>
                  {this.props.error && (
                    <Alert
                      type="error"
                      msg="There was an error processing this email change. Please reach out to engineering for assistance."
                    />
                  )}
                  <Button
                    type="submit"
                    name="submit"
                    btnLabel="Submit for Approval"
                    loading={this.props.isUpdatingEmail}
                    withArrow
                  />
                </form>
              )}
            </Formik>
          </SimpleModal>
        )}
        {this.state.showEditProfileModal && (
          // @ts-expect-error buttonText is required on SimpleModalProps but
          // omitted here — inert because hideActions suppresses the submit
          // button. Preserved verbatim from the original JS.
          <SimpleModal
            show={this.state.showEditProfileModal}
            title="Edit User Profile"
            onClose={() => this.setState({ showEditProfileModal: false })}
            showCloseButton
            hideActions
            size="lg"
          >
            <AdminUserProfileWorkflow
              userId={this.props.userId}
              userInfo={
                get(this.state.user, "userProfile", {}) as Record<
                  string,
                  string
                >
              }
              onCancel={() => this.setState({ showEditProfileModal: false })}
              onSuccess={async () => {
                this.setState({ showEditProfileModal: false });
                await this._getAndSetUser();
              }}
            />
          </SimpleModal>
        )}
        {this.state.showResetConfirmModal && (
          // @ts-expect-error children is required on SimpleModalProps but this
          // confirm modal is self-closing — inert (it relies on title/subtext/
          // buttonText only). Preserved verbatim from the original JS.
          <SimpleModal
            show={this.state.showResetConfirmModal}
            title="Reset User"
            subtext="Are you sure you want to reset this user's state? This action will erase all of their application data."
            onClose={() => this.setState({ showResetConfirmModal: false })}
            showCloseButton
            closeButtonText="Cancel"
            buttonText="Reset"
            isFetching={this.props.isResetingUser}
            onSubmit={this._resetUserState}
          />
        )}
        {this.state.showConfirmAdvanceAlloyModal && (
          <AdvanceAlloyConfirmationModal
            entityType="USER"
            entityId={this.props.userId as string}
            // @ts-expect-error withApollo wraps the modal as Omit<Props, "client">
            // and injects its own client, so this explicit client prop no longer
            // type-checks. Inert (the HOC's injected client shadows it) and
            // preserved verbatim from the original JS.
            client={this.props.client}
            onClose={() => {
              this.setState({ showConfirmAdvanceAlloyModal: false });
            }}
          />
        )}
        <Card.Body>
          {isPaychex && (
            <div style={{ textAlign: "right", marginBottom: 24 }}>
              <img className="icon-logo" src="/assets/paychex_logo.svg" />
            </div>
          )}
          <InfoTable
            metadata={this._getTableMetadata()}
            data={this.state.user}
          />
          {this.props.error && <Alert type={"error"} msg={this.props.error} />}
          <div
            className="btn-row"
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            {this._canEditProfile() && (
              <Button
                type="button"
                name="submit"
                btnLabel="Edit Profile"
                onClick={() => this.setState({ showEditProfileModal: true })}
                size={"sm"}
                withArrow
              />
            )}
            {this._getActionsContent()}
            <Link
              to={`/workflows?entityId=${String(this.props.userId)}`}
              className="icon-link"
            >
              View Workflows
            </Link>
          </div>
        </Card.Body>
      </>
    );
  }
}

export default connector(withApollo<Props>(UserInfo));
